import React, { useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";

import ReadMoreLess from "../ReadMoreLess";
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
} from "@mui/x-data-grid-pro";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ValidationSummaryDataGrid from "../DataGrids/ValidationSummaryDataGrid";
import ResourceSupportDocuments from "../Organisms/ResourceSupportDocuments";
import AdhocQueryDashboardTab from "./AdhocQueryDashboardTab";
import Incidents from "../Rules/Incidents";
import Loader from "../Molecules/Loader/Loader";
import ValidationResultHeader from "../Molecules/Resource/ValidationResultHeader";
import ValidationResultResourceStatistics from "../Molecules/Resource/ValidationResultResourceStatistics";
import ValidationResultParameters from "../Molecules/Resource/ValidationResultParameters";
import ValidationResultVariables from "./ValidationResultVariables";
import useFetchPaginatedValidationErrors from "../../hooks/useFetchPaginatedValidationErrors";
import DuplicateRecords from "./DuplicateRecords";
import NullKeyErrors from "./NullKeyErrors";
import {
  IconIncidentDetails,
  IconIncidentResources,
  IconStatusFail,
  IconStatusInprogress,
  IconStatusNew,
  IconStatusPass,
} from "../../common/utils/icons";
import DashboardUserDetails from "../Molecules/Rule/DashboardUserDetails";
import IncidentUserandStatusDialog from "../Molecules/Rule/IncidentUserandStatusDialog";
import useFetchUsersList from "../../hooks/useFetchUsersList";
import { useAuth } from "../../contexts/AuthContext";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import StatusCell from "../../common/utils/statusIcon";
import {
  ClassificationColumn,
  CommentColumn,
  CurrentStatusColumn,
  ReasonColumn,
  UserColumn,
} from "../../common/utils/commonActionsFields";
import { useClassificationContext } from "../../contexts/ClassificationContext";
import useFetchAllClassifications from "../../hooks/useFetchAllClassifications";

import {
  calculateDaysInQueue,
  formatNumberWithCommas,
  getFormattedDateTime,
} from "../../services/utils";
import { keyframes } from "@mui/system";
import {
  LOW_CHIP_COLOR,
  MED_CHIP_COLOR,
  HIGH_CHIP_COLOR,
} from "../Scoring/scoreTheme";
import { getConfig } from "../../config/runtimeConfig";

/** Subtle breathing animation for severity pulse dots. */
const pulse = keyframes`
  0%   { transform: scale(0.95); opacity: 0.7; }
  50%  { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.7; }
`;

/** Map severity string to its theme color. */
const getSeverityDotColor = (
  severity: string | null | undefined,
): string | null => {
  if (!severity) return null;
  const s = severity.toLowerCase();
  if (s.includes("low")) return LOW_CHIP_COLOR;
  if (s.includes("medium") || s.includes("med")) return MED_CHIP_COLOR;
  if (s.includes("high")) return HIGH_CHIP_COLOR;
  return null;
};

interface ISummarizesValidationResults {
  validateResult: any;
  isLoading: boolean;
  setIsLoading: any;
  isBackdropLoading: boolean;
  currentIncidentData: any;
  setIsTriggereBtnPressed: any;
  setIsCommentBtnPressed: any;
  currentResourceResultId: any;
}
const initialDashboardData = {
  status: false,
  assigned_user: "",
  issueId: null,
  isBackDropVisible: false,
};

const SummarizesValidationResults = ({
  validateResult,
  isLoading,
  setIsLoading,
  isBackdropLoading,
  currentIncidentData,
  setIsTriggereBtnPressed,
  setIsCommentBtnPressed,
  currentResourceResultId,
}: ISummarizesValidationResults) => {
  const { hasPermission } = useAuth();

  // Permission checks for validation result actions
  const canEditValidationIssues = hasPermission("validation_issues", "edit");
  const [rowsData, setRowsData] = useState<any>([]);
  const [columnsData, setColumnsData] = useState<any>([]);
  const [gridColumnLength, setGridColumnLength] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<string>("validationErrorsSummary");
  const [selectedColumnName, setSelectedColumnName] = useState<any>("");
  const [dashboardUserDetailsData, setDashboardUserDetailsData] =
    useState(initialDashboardData);
  useEffect(() => {
    if (dashboardUserDetailsData?.status === true) {
      setDashboardUserDetailsData(initialDashboardData);
    }
  }, [activeTab]);

  const [userDialogData, setUserDialogData] = useState<any>({
    status: false,
    dialogName: "",
    selectedIds: [],
  });
  const [allUsersList, setallUsersList] = useState<any>([]);
  const [selectedIds, setSelectedIds] = useState<any>([]);

  // Fetch classifications once and store in shared context (same pattern as TagAndSystemProvider)
  const { setAllClassifications, setIsClassificationsLoading } =
    useClassificationContext();
  const [fetchedClassifications] = useFetchAllClassifications({
    setIsLoading: setIsClassificationsLoading,
  });

  useEffect(() => {
    if (fetchedClassifications.length > 0) {
      setAllClassifications(fetchedClassifications);
    }
  }, [fetchedClassifications, setAllClassifications]);

  //for pagination default values and states
  const defaultPage = getConfig("REACT_APP_DEFAULT_PAGE", "1");
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1,
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);

  useEffect(() => {
    setUserDialogData((prev: any) => ({
      ...prev,
      selectedIds: selectedIds || [],
    }));
  }, [selectedIds]);
  const [validationLoading, setValidationLoading] = useState(false);

  const [validationErrorsData] = useFetchPaginatedValidationErrors({
    currentValidationResultId: validateResult?.id,
    setIsLoading: setValidationLoading,
    page,
    pSize,
    isDetailedExecutionDataAvailable:
      validateResult?.is_detailed_execution_data_available,
    isIssuesDataAvailable: validateResult?.is_issues_data_available ?? false,
    selectedColumnName,
  });

  const [usersList] = useFetchUsersList({
    setIsLoading,
  });

  useEffect(() => {
    setallUsersList(usersList);
  }, [usersList]);

  const getFormattedKeysPairs = (inputString: string) => {
    const pairs: any = {};
    inputString.split(",").forEach((pair) => {
      const [key, value] = pair.trim().split("=");
      pairs[key] = value;
    });
    return pairs;
  };

  const handelRowData = (validationErrors: any) => {
    const isIssuesDataAvailable =
      validateResult?.is_issues_data_available ?? false;

    const rows: any =
      validationErrors?.map((item: any, idx: any) => {
        if (isIssuesDataAvailable) {
          // Handle "WithIssue" endpoint data structure
          const { resource_keys, file_name, ...rest } =
            item?.validation_error_record_details ?? {};
          const newArr = file_name?.replace(/'/g, "").split(";") || [];

          // Calculate days in queue
          const diffInDays = calculateDaysInQueue(item.first_occurrence);

          return {
            id: idx,
            rowId: item?.id,
            assigned_user: item?.assigned_user,
            current_status: item?.current_status,
            assigned_by: item?.assigned_by,
            comment: item?.comment,
            first_occurrence: item?.first_occurrence,
            days_in_queue: diffInDays,
            issue_classification_id: item?.issue_classification_id,
            issue_reason_id: item?.issue_reason_id,
            ...item?.validation_error_record_details,
            ...resource_keys,
            ...rest,
            ...item?.validation_error_record_details?.record_snapshot
              ?.record_data,
            file_name:
              newArr.length > 1
                ? `<div class="word-break-all"><ul class="ul-inside-grid">${newArr
                    .map((arr: any) => `<li>${arr}</li>`)
                    .join("")}</ul></div>`
                : `<div class="word-break-all">${newArr[0] || ""}</div>`,
          };
        } else {
          // Handle "WithoutIssue" endpoint data structure
          const { resource_keys, file_name, ...rest } = item ?? {};
          const newArr = file_name?.replace(/'/g, "").split(";") || [];

          // Calculate days in queue
          const diffInDays = calculateDaysInQueue(item.first_occurrence);

          return {
            id: idx,
            rowId: item?.id,
            ...item,
            ...resource_keys,
            first_occurrence: item?.first_occurrence,
            days_in_queue: diffInDays,
            ...item?.record_snapshot?.record_data,
            file_name:
              newArr.length > 1
                ? `<div class="word-break-all"><ul class="ul-inside-grid">${newArr
                    .map((arr: any) => `<li>${arr}</li>`)
                    .join("")}</ul></div>`
                : `<div class="word-break-all">${newArr[0] || ""}</div>`,
          };
        }
      }) || [];

    setRowsData(rows);
  };

  const handelSummaryRowData = (validation_errors: any) => {
    const { resource_keys, key_column_values } = validation_errors || {};
    if (key_column_values?.length > 0 && resource_keys?.length > 0) {
      const rowsData = key_column_values.map((values: any, index: any) => {
        const rowObject: any = {};
        resource_keys.forEach((column: any, columnIndex: any) => {
          rowObject[column] = values[columnIndex];
        });
        rowObject.id = index + 1;
        return rowObject;
      });
      setRowsData(rowsData);
    }
  };

  const getColumns = (validation_errors: any) => {
    const isIssuesDataAvailable =
      validateResult?.is_issues_data_available ?? true;

    let recordData: any,
      firstColumns: any[] = [],
      secondColumns: any[] = [];

    if (isIssuesDataAvailable) {
      // Handle "WithIssue" endpoint data structure
      recordData =
        validation_errors[0]?.validation_error_record_details?.record_snapshot
          ?.record_data;
      firstColumns = Object.keys(
        validation_errors[0]?.validation_error_record_details?.resource_keys ||
          {},
      );
      secondColumns = Object.keys(
        validation_errors[0]?.validation_error_record_details || {},
      ).filter(
        (item) => item === "custom_validation_name" || item === "error_message",
      );
    } else {
      // Handle "WithoutIssue" endpoint data structure
      recordData = validation_errors[0]?.record_snapshot?.record_data;
      firstColumns = Object.keys(validation_errors[0]?.resource_keys || {});
      secondColumns = Object.keys(validation_errors[0] || {}).filter(
        (item) => item === "custom_validation_name" || item === "error_message",
      );
    }

    let columnsData: any = [];
    if (validation_errors && validation_errors.length > 0) {
      setGridColumnLength(firstColumns.length);

      const columns: any = [
        ...firstColumns.map((item: any) => {
          setGridColumnLength(firstColumns.length);
          return {
            field: item,
            headerName: item.toUpperCase(),
            flex: 1,
            minWidth: 120,
          };
        }),

        ...secondColumns.map((item: any) => {
          let newItem = item.replace(/^['"]|['"]$/g, "");
          return {
            field: newItem,
            headerName: newItem.toUpperCase(),
            flex: 1,
            minWidth: 280,
            renderCell: (params: any) => (
              <>
                {params.value && params.value.length > 100 ? (
                  <ReadMoreLess data={params?.value?.replace(/"/g, "")} />
                ) : (
                  <Box className="word-break-all">
                    {typeof params?.value === "string"
                      ? params.value.replace(/"/g, "")
                      : params?.value}
                  </Box>
                )}
              </>
            ),
          };
        }),

        {
          field: "file_name",
          headerName: "FILE_NAME(S)",
          flex: 1,
          minWidth: 240,
          renderCell: (params: any) => (
            <div
              className="data-grid-cell word-break-all"
              dangerouslySetInnerHTML={{ __html: params.value }}
            />
          ),
        },
      ];

      const daysInQueueColumn: any = [
        {
          field: "days_in_queue",
          headerName: "Age",
          flex: 1,
          minWidth: 110,
          renderCell: (params: any) => {
            return (
              <Box>
                {params?.row?.first_occurrence === null ? (
                  (params.value ?? 0)
                ) : (
                  <Tooltip
                    title={
                      <Typography
                        sx={{ fontSize: "0.875rem" }}
                      >{`First Occurrence : ${getFormattedDateTime(
                        params?.row?.first_occurrence,
                      )}`}</Typography>
                    }
                    placement="top"
                    className="tooltip-ellipsis"
                  >
                    {params.value ?? 0}
                  </Tooltip>
                )}
              </Box>
            );
          },
        },
      ];

      const issueColumns: any = [
        CurrentStatusColumn({}),
        UserColumn({}),
        CommentColumn({}),
        ClassificationColumn({}),
        ReasonColumn({}),
        {
          field: "action",
          headerName: "Actions",
          align: "center",
          headerAlign: "center",
          minWidth: "130",
          pinned: "right",
          renderCell: (params: any, index: any) => {
            return (
              <>
                <Box sx={{ display: "inline-flex", columnGap: "12px" }}>
                  {canEditValidationIssues &&
                    validateResult?.is_issues_data_available !== false && (
                      <Tooltip
                        title={`Incident Details : ${params?.row?.id}`}
                        placement="top"
                        arrow
                      >
                        <Button
                          className="btn-blue btn-orange btn-sm"
                          onClick={(event: { stopPropagation: () => void }) => {
                            event.stopPropagation();
                            setDashboardUserDetailsData((prev: any) => {
                              return {
                                ...prev,
                                status: true,
                                incidentId: params.row.rowId,
                                assigned_user: params.row.assigned_user,
                                panelType: "incidentDetail",
                                isBackDropVisible: true,
                                first_occurrence: params.row.first_occurrence,
                                issue_classification_id:
                                  params.row.issue_classification_id,
                                issue_reason_id: params.row.issue_reason_id,
                                comment: [
                                  {
                                    comment: params.row.comment,
                                    create_date: params.row.create_date,
                                    assigned_user:
                                      params.row?.assigned_user || "",
                                  },
                                ],
                                current_status: params.row.current_status,
                                issueId: params.row.rowId,
                              };
                            });
                          }}
                        >
                          <IconIncidentDetails />
                        </Button>
                      </Tooltip>
                    )}
                </Box>
              </>
            );
          },
        },
      ];

      // Conditionally include issueColumns based on is_issues_data_available flag
      if (isIssuesDataAvailable) {
        columnsData = [...columns, ...daysInQueueColumn, ...issueColumns];
      } else {
        columnsData = [...columns];
      }

      if (recordData) {
        const snapshot_columns = recordData
          ? Object.keys(recordData)
              .filter(
                (key) =>
                  !firstColumns.includes(key) &&
                  !secondColumns.includes(key) &&
                  key !== "file_name" &&
                  key !== "ErrorMessage",
              )
              .map((key) => ({
                field: key,
                headerName: key,
                flex: 1,
                minWidth: 120,
              }))
          : [];

        columnsData = [...columnsData, ...snapshot_columns];
      }
      setColumnsData(columnsData);
    }
  };

  const getSummaryColumns = (validation_errors: any) => {
    const { resource_keys } = validation_errors || {};
    if (resource_keys?.length > 0) {
      const columns: any = resource_keys.map((item: any) => {
        return {
          field: item,
          headerName: item.toUpperCase(),
          flex: 1,
          maxWidth: 160,
        };
      });
      setColumnsData([
        {
          ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
          renderCell: (params: any) => {
            const customValidationKeys = Object.keys(params?.row || {}).filter(
              (key) => key.includes("_custom_validation"),
            );
            const isCustomValidation = customValidationKeys.every(
              (key) =>
                params?.row.hasOwnProperty(key) &&
                (params?.row[key] === null || params?.row[key] === ""),
            );
            return (
              <>
                {!isCustomValidation && (
                  <IconButton
                    size="small"
                    tabIndex={-1}
                    className="custom-validation-arrow"
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                )}
              </>
            );
          },
        },
        ...columns,
      ]);
    }
  };

  const validationColumns: any = [
    {
      field: "__status",
      headerName: "",
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        const dotColor = getSeverityDotColor(params?.row?.validation_severity);
        if (!dotColor) return null;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: dotColor,
                animation: `${pulse} 2s ease-in-out infinite`,
              }}
            />
          </Box>
        );
      },
    },
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      renderCell: (params: any) => {
        const columnName = params?.row?.column_name;

        const handleClick = () => {
          if (selectedColumnName === columnName) {
            setSelectedColumnName("");
          } else {
            setSelectedColumnName(columnName);
          }
        };
        return (
          !validateResult?.additional_properties?.summary_mode && (
            <IconButton
              size="small"
              tabIndex={-1}
              className="custom-validation-arrow"
              onClick={handleClick}
            >
              <ExpandMoreIcon />
            </IconButton>
          )
        );
      },
    },
    {
      field: "column_name",
      headerName: "Column Name",
      flex: 1,
      maxWidth: 280,
    },
    {
      field: "total_validation_errors",
      headerName: "Total Validation Errors",
      flex: 1,
      maxWidth: 280,
      renderCell: (params: any) => {
        const value = params.value;
        return value !== undefined && value !== null
          ? formatNumberWithCommas(value)
          : "";
      },
    },
    {
      field: "total_general_validation_errors",
      headerName: "Total General Validation Errors",
      flex: 1,
      maxWidth: 280,
      valueGetter: (params: any) => {
        const dict = params?.row?.general_validation_errors;
        if (!dict) return 0;
        return Object.values(dict).reduce(
          (sum: number, v: any) => sum + (Number(v) || 0),
          0,
        );
      },
      renderCell: (params: any) => {
        const value = params.value;
        return value !== undefined && value !== null
          ? formatNumberWithCommas(value)
          : "";
      },
    },
    {
      field: "total_custom_validation_errors",
      headerName: "Total Custom Validation Errors",
      flex: 1,
      maxWidth: 280,
      valueGetter: (params: any) => {
        const arr = params?.row?.custom_validation_errros;
        if (!Array.isArray(arr) || arr.length === 0) return 0;
        return arr.reduce(
          (sum: number, v: any) =>
            sum + (Number(v?.total_validation_errors) || 0),
          0,
        );
      },
      renderCell: (params: any) => {
        const value = params.value;
        return value !== undefined && value !== null
          ? formatNumberWithCommas(value)
          : "";
      },
    },
    {
      field: "validation_severity",
      headerName: "Validation Severity",
      flex: 1,
      maxWidth: 280,
      renderCell: (params: any) => {
        const transformedValue = params.value
          .split("_")
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        return params.value;
      },
    },
  ];

  const validationRows =
    validateResult?.additional_properties?.validation_errors_summary?.map(
      (item: any, index: any) => ({
        id: index,
        ...item,
      }),
    ) || [];

  useEffect(() => {
    if (validateResult && validationErrorsData?.items?.length > 0) {
      if (validateResult.additional_properties?.summary_mode) {
        getSummaryColumns(validationErrorsData);
        handelSummaryRowData(validationErrorsData.items);
      } else {
        getColumns(validationErrorsData.items);
        handelRowData(validationErrorsData.items);
      }
    } else {
      console.log("⚠️ No data to process:", {
        hasValidateResult: !!validateResult,
        hasItems: !!validationErrorsData?.items?.length,
      });
    }
  }, [validateResult, validationErrorsData]);

  const [isAllColumnPinned, setIsAllColumnPinned] = useState(false);
  const [pinnedColumns, setPinnedColumns] = useState({
    left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
    right: ["current_status", "username", "comment", "action"],
  });
  const handleMoveColumns = () => {
    setPinnedColumns((prevState) => {
      const columnsToAdd = ["current_status", "username", "comment"];
      const isColumnsAvailable = columnsToAdd.every((item) =>
        prevState.right.includes(item),
      );
      setIsAllColumnPinned(isColumnsAvailable);
      if (isColumnsAvailable) {
        return {
          left: prevState.left,
          right: prevState.right.filter((item) => !columnsToAdd.includes(item)),
        };
      } else {
        return {
          left: [...prevState.left],
          right: [...columnsToAdd, ...prevState.right],
        };
      }
    });
  };

  const tabContent: any = {
    validationErrorsSummary: (
      <>
        {validationRows && validationRows?.length !== 0 ? (
          <Box className="position-relative">
            {validationLoading && (
              <>
                <Loader isAbsolute={true} isLoading={validationLoading} />
              </>
            )}

            <ValidationSummaryDataGrid
              dataColumns={validationColumns}
              dataRows={validationRows}
              loading={validationLoading}
              setLoading={setValidationLoading}
              isExportButtonRequired={true}
              className="dataTable no-radius hide-progress-icon"
              rowCount={rowsData?.total}
              historyType={"Validation"}
              disableColumnFilter={false}
              validationErrorProps={{
                dataRows: rowsData,
                dataColumns: columnsData,
                loading: isLoading,
                className:
                  "dataTable no-radius datatable-column-sep checkbox-result-column remove-pinnedColumnHeaders-bg pinnedColumnHeaders-bg-height hide-progress-icon",
                gridColumnLength: gridColumnLength,
                dataListTitle: "",
                isHeight100: true,

                pinnedColumns,
                handleMoveColumns,
                isAllColumnPinned,

                disableColumnPinning: false,
                validationCheckboxSelection:
                  validateResult?.is_issues_data_available ?? false,
                selectedIds: selectedIds,
                setSelectedIds: setSelectedIds,
                validationPaginationMode: "server",
                validationRowCount: validationErrorsData?.total || 0,
                validationPaginationModel: {
                  page: page - 1,
                  pageSize: pSize,
                },
                validationOnPaginationModelChange: (params: any) => {
                  if (params.pageSize !== pSize || params.page !== page - 1) {
                    setPage(params.page + 1);
                    setPSize(params.pageSize);
                  }
                },
                isIssuesDataAvailable:
                  validateResult?.is_issues_data_available ?? true,
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              paddingTop: "24px",
              paddingBottom: "24px",
              display: "flex",
              justifyContent: "center",
            }}
            className="adhock-query-accordion-wrapper"
          >
            No data available
          </Box>
        )}
      </>
    ),
    crossFieldValidations: (
      <Box>
        <AdhocQueryDashboardTab
          validateResult={validateResult}
          adhocQueryData={
            validateResult?.additional_properties
              ?.cross_field_validation_results || []
          }
        />
      </Box>
    ),
    duplicateRecords: (
      <Box>
        <DuplicateRecords
          validateResult={validateResult}
          userDialogData={userDialogData}
          setUserDialogData={setUserDialogData}
          allUsersList={allUsersList}
        />
      </Box>
    ),
    nullKeyErrors: (
      <Box>
        <NullKeyErrors
          validateResult={validateResult}
          userDialogData={userDialogData}
          setUserDialogData={setUserDialogData}
          allUsersList={allUsersList}
        />
      </Box>
    ),
  };
  const handleChangeTab = (event: any, newValue: any) => {
    setActiveTab(newValue);
    setSelectedColumnName("");
    setPage(defaultPage ? parseInt(defaultPage) : 1);
    if (newValue !== "validationErrorsSummary" && selectedIds.length > 0) {
      setSelectedIds([]);
    }
  };

  return (
    <>
      <Loader isLoading={isLoading} />
      <div className="validate-page">
        {/* Header */}
        <ValidationResultHeader validateResult={validateResult} />

        {/* Resource Statistics */}
        <ValidationResultResourceStatistics validateResult={validateResult} />

        {/* Incidents */}
        <Incidents
          currentResultId={currentResourceResultId || ""}
          currentIncidentData={currentIncidentData ?? []}
          setIsLoading={setIsLoading}
          setIsTriggereBtnPressed={setIsTriggereBtnPressed}
          setIsCommentBtnPressed={setIsCommentBtnPressed}
        />

        {/* Validation Parameters */}
        {validateResult?.complete_validation_params
          ?.validation_request_body && (
          <ValidationResultParameters validateResult={validateResult} />
        )}

        {/* Supporting Documents */}
        {validateResult?.additional_properties?.supporting_documents && (
          <div className="accordion-panel mt-8">
            <ResourceSupportDocuments
              title="Supporting Documents"
              documents={
                validateResult?.additional_properties?.supporting_documents
              }
            />
          </div>
        )}

        {/* Resource Variables */}
        {validateResult?.complete_validation_params?.validation_request_body
          ?.inline_variables && (
          <ValidationResultVariables
            resourceVariables={
              validateResult?.complete_validation_params
                ?.validation_request_body?.inline_variables
            }
          />
        )}
        <Grid container className="tabs-with-butttons">
          <Grid item xs>
            <Tabs
              sx={{
                mt: 1,
              }}
              value={activeTab}
              onChange={handleChangeTab}
              className="mui-tabs alternative-1"
              variant="scrollable"
            >
              <Tab
                label="Validation Errors Summary"
                value="validationErrorsSummary"
              />
              <Tab label="Adhoc Query" value="crossFieldValidations" />
              <Tab label="Duplicate Records" value="duplicateRecords" />
              <Tab label="Null Key Errors" value="nullKeyErrors" />
            </Tabs>
          </Grid>
          {canEditValidationIssues &&
            validateResult?.is_issues_data_available &&
            activeTab !== "crossFieldValidations" &&
            (activeTab !== "nullKeyErrors" ||
              userDialogData?.hasNullKeyErrorsData) &&
            (activeTab !== "duplicateRecords" ||
              userDialogData?.hasDuplicateRecordsData) && (
              <Grid item className="buttons-group">
                <Button
                  className="btn-blue btn-orange btn-sm"
                  onClick={() => {
                    setUserDialogData((prev: any) => ({
                      ...prev,
                      status: true,
                      dialogName: "Update Issue",
                    }));
                  }}
                  disabled={userDialogData?.selectedIds?.length === 0}
                >
                  Update Issue
                </Button>
              </Grid>
            )}
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
          <Box>{tabContent[activeTab]}</Box>
        </Grid>
      </div>
      {activeTab === "validationErrorsSummary" && (
        <>
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={dashboardUserDetailsData?.isBackDropVisible ?? false}
          ></Backdrop>
          <DashboardUserDetails
            dashboardUserDetailsData={dashboardUserDetailsData}
            setDashboardUserDetailsData={setDashboardUserDetailsData}
            allUsersList={allUsersList}
            setIsLoading={setIsLoading}
            setDetailViewDataRows={setRowsData}
            issueFrom={"validation"}
          />
          <IncidentUserandStatusDialog
            userDialogData={userDialogData}
            setUserDialogData={setUserDialogData}
            allUsersList={allUsersList}
            setDetailViewDataRows={setRowsData}
            issueFrom={"validation"}
            setSelectedIds={setSelectedIds}
          />
        </>
      )}
    </>
  );
};

export default SummarizesValidationResults;
